import { useCallback } from 'react';
import { notFound, useRouter } from '@tanstack/react-router';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { useClubCardQuery } from '@features/club/api/hooks';
import { ClubCardPageProps } from '@features/club/pages/ClubCardPage/index';
import {
  useCreateMatchMutation,
  useCreateTournamentMutation,
} from '@features/tournament/api/hooks';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';

type Params = {
  clubId: string;
};

export function useClubCard({ clubId }: Params): ClubCardPageProps {
  const { data: club, isPending, failureReason } = useClubCardQuery(clubId);
  if (failureReason && 'httpCode' in failureReason && failureReason.httpCode === 404) {
    throw notFound();
  }
  const { mutateAsync: createTournament } = useCreateTournamentMutation();
  const { mutateAsync: createMatch } = useCreateMatchMutation();
  const { isManager } = useClubRoles(clubId);
  const isAdmin = useIsAdmin();

  const router = useRouter();

  const onTournamentCreate = useCallback(async () => {
    if (!clubId) return;
    const result = await createTournament({ clubId });
    if (result?.tournamentId) {
      router.navigate({
        to: '/club/$clubId/tournament/$tournamentId/edit',
        params: { clubId, tournamentId: result.tournamentId },
        from: '/',
      });
    }
  }, [router, clubId, createTournament]);

  const onMatchCreate = useCallback(async () => {
    if (!clubId) return;
    const result = await createMatch({ clubId });
    if (result?.tournamentId) {
      router.navigate({
        to: '/club/$clubId/tournament/$tournamentId/edit',
        params: { clubId, tournamentId: result.tournamentId },
        from: '/',
      });
    }
  }, [router, clubId, createMatch]);

  const onSettings = () => {
    router.navigate({
      to: '/club/$clubId/settings',
      params: { clubId },
      from: '/',
    });
  };

  return {
    club: club && {
      ...club,
      logoUrl: club?.logoUrl || null,
      themeColor: club?.themeColor || null,
    },
    isPending,
    isManager,
    isAdmin,
    onTournamentCreate,
    onMatchCreate,
    onSettings,
  };
}
